import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [licenseStatus, setLicenseStatus] = useState(null);
  const isRefreshing = React.useRef(false);
  const refreshQueue = React.useRef([]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Interceptor para lidar com sessão expirada e refresh automático de token
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Converte Blob -> JSON no inicio (para blob requests que falharam com erro HTTP
        // o axios deixa o data como Blob, e acessar .detail em Blob dispara erro XHR
        // "Failed to read responseText". Convertemos aqui uma unica vez para TODOS handlers.)
        if (error?.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            try {
              error.response.data = JSON.parse(text);
            } catch {
              error.response.data = text;
            }
          } catch {
            // falha na conversao - deixa como esta
          }
        }
        
        const originalRequest = error.config;
        
        // Se sessão encerrada por outro login, logout imediato
        if (error.response?.status === 401 && 
            error.response?.data?.detail === "Sessão encerrada. Faça login novamente.") {
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          setToken(null);
          setUser(null);
          delete axios.defaults.headers.common['Authorization'];
          return Promise.reject(error);
        }
        
        // Licenca suspensa - ativa o bloqueio imediatamente (gate no Layout)
        if (error.response?.status === 403 &&
            error.response?.data?.detail === 'license_suspended') {
          setLicenseStatus((prev) => ({ ...(prev || {}), activated: true, status: 'suspended' }));
          return Promise.reject(error);
        }
        
        // Se token expirado e temos refresh_token, tentar renovar
        if (error.response?.status === 401 && 
            error.response?.data?.detail === "Token expirado" &&
            !originalRequest._retry) {
          
          const storedRefresh = localStorage.getItem('refresh_token');
          if (!storedRefresh) {
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
            delete axios.defaults.headers.common['Authorization'];
            return Promise.reject(error);
          }
          
          // Se já está renovando, enfileirar a request
          if (isRefreshing.current) {
            return new Promise((resolve, reject) => {
              refreshQueue.current.push({ resolve, reject, config: originalRequest });
            });
          }
          
          originalRequest._retry = true;
          isRefreshing.current = true;
          
          try {
            const res = await axios.post(`${API}/auth/refresh`, { refresh_token: storedRefresh });
            const newToken = res.data.access_token;
            
            localStorage.setItem('token', newToken);
            setToken(newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            
            // Processar fila de requests pendentes
            refreshQueue.current.forEach(({ resolve, config }) => {
              config.headers['Authorization'] = `Bearer ${newToken}`;
              resolve(axios(config));
            });
            refreshQueue.current = [];
            
            return axios(originalRequest);
          } catch (refreshError) {
            // Refresh falhou - logout
            localStorage.removeItem('token');
            localStorage.removeItem('refresh_token');
            setToken(null);
            setUser(null);
            delete axios.defaults.headers.common['Authorization'];
            
            // Rejeitar requests pendentes
            refreshQueue.current.forEach(({ reject }) => reject(refreshError));
            refreshQueue.current = [];
            
            return Promise.reject(refreshError);
          } finally {
            isRefreshing.current = false;
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Detectar remoção do token por outro interceptor (api.js)
  useEffect(() => {
    const checkToken = () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken && token) {
        setToken(null);
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
      }
    };

    window.addEventListener('storage', checkToken);
    const interval = setInterval(checkToken, 2000);

    return () => {
      window.removeEventListener('storage', checkToken);
      clearInterval(interval);
    };
  }, [token]);

  // Re-verifica o status da licenca periodicamente (bloqueio quase em tempo real)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(fetchLicenseStatus, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const fetchLicenseStatus = async () => {
    try {
      const licRes = await axios.get(`${API}/license/status`);
      setLicenseStatus(licRes.data);
      return licRes.data;
    } catch (e) {
      console.log('License status not available');
      return null;
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      await fetchLicenseStatus();
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      // Verificar se requer confirmação de sessão
      if (response.data.requires_confirmation) {
        return { 
          success: false, 
          requiresConfirmation: true,
          message: response.data.message,
          userName: response.data.user_name,
          lastLogin: response.data.last_login
        };
      }
      
      const { access_token, refresh_token: rt, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('refresh_token', rt);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      fetchLicenseStatus();
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erro ao fazer login' 
      };
    }
  };

  const loginForceSession = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login/force`, { 
        email, 
        password,
        force: true 
      });
      
      const { access_token, refresh_token: rt, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('refresh_token', rt);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      fetchLicenseStatus();
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Senha incorreta' 
      };
    }
  };

  const logout = async () => {
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`);
      } catch (error) {
        console.log('Logout error (ignorado):', error);
      }
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  // Registra o aceite dos Termos de Uso e da Politica de Privacidade
  const acceptTerms = async () => {
    try {
      const res = await axios.post(`${API}/auth/accept-terms`);
      setUser((prev) => prev ? {
        ...prev,
        terms_accepted: true,
        terms_accepted_at: res.data.terms_accepted_at,
        terms_version: res.data.terms_version
      } : prev);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erro ao registrar o aceite' };
    }
  };

  // Indica se o usuario ainda precisa aceitar os Termos/Politica (atual ou nova versao)
  const needsTermsAcceptance = () => {
    if (!user) return false;
    if (!user.terms_accepted) return true;
    const current = user.current_terms_version;
    if (current && user.terms_version !== current) return true;
    return false;
  };

  // Hierarquia de roles
  const ROLE_LEVELS = { master: 5, admin_geral: 4, admin: 3, marcador_geral: 2, marcador: 1, relator_hospital: 1, relator_lab_municipal: 1 };
  const getRoleLevel = (role) => ROLE_LEVELS[role] || 0;

  const isMaster = () => user?.role === 'master';
  const isAdminGeral = () => getRoleLevel(user?.role) >= getRoleLevel('admin_geral') || isSecretaria();
  const isAdmin = () => getRoleLevel(user?.role) >= getRoleLevel('admin');
  const isMarcadorGeral = () => user?.role === 'marcador_geral' || user?.is_marcador_geral === true;
  const isSecretaria = () => user?.is_secretaria === true;
  const isRelator = () => user?.role === 'relator_hospital' || user?.role === 'relator_lab_municipal';
  const getRelatorOrigin = () => {
    if (user?.role === 'relator_hospital') return 'hospital';
    if (user?.role === 'relator_lab_municipal') return 'lab_municipal';
    return null;
  };
  
  // Acesso completo a pacientes/unidades (master, admin_geral, admin, marcador_geral, secretaria)
  const hasFullAccess = () => getRoleLevel(user?.role) >= getRoleLevel('marcador_geral') || isSecretaria();
  
  // Pode ver vagas (todos os perfis exceto Relator)
  const canViewSlots = () => !isRelator() && getRoleLevel(user?.role) >= getRoleLevel('marcador');
  
  // Pode editar vagas (admin_geral+ APENAS - NÃO admin secretaria)
  const canEditSlots = () => getRoleLevel(user?.role) >= getRoleLevel('admin_geral');
  
  // Pode ver relatórios (marcador_geral+ ou secretaria ou Relator)
  const canViewReports = () => isRelator() || getRoleLevel(user?.role) >= getRoleLevel('marcador_geral') || isSecretaria();
  
  // Pode ver auditoria (admin_geral+ ou secretaria)
  const canViewAudit = () => !isRelator() && (getRoleLevel(user?.role) >= getRoleLevel('admin_geral') || isSecretaria());
  
  // Pode gerenciar usuarios (admin+) - Relator NAO
  const canManageUsers = () => !isRelator() && getRoleLevel(user?.role) >= getRoleLevel('admin');
  
  // Pode ver unidades (admin_geral+ ou secretaria)
  const canViewUnits = () => !isRelator() && (getRoleLevel(user?.role) >= getRoleLevel('admin_geral') || isSecretaria());
  
  // Pode ver procedimentos (admin_geral+ ou secretaria)
  const canViewProcedimentos = () => !isRelator() && (getRoleLevel(user?.role) >= getRoleLevel('admin_geral') || isSecretaria());
  
  // Pode ver configurações (admin_geral+ ou secretaria)
  const canViewSettings = () => !isRelator() && (getRoleLevel(user?.role) >= getRoleLevel('admin_geral') || isSecretaria());
  
  // Pode acessar Dashboard (todos exceto Relator)
  const canViewDashboard = () => !isRelator();

  // Pode ver pacientes (todos exceto Relator)
  const canViewPatients = () => !isRelator() && getRoleLevel(user?.role) >= getRoleLevel('marcador');

  // Pode ver consultas (todos exceto Relator)
  const canViewAppointments = () => !isRelator() && getRoleLevel(user?.role) >= getRoleLevel('marcador');
  
  // Pode bloquear/excluir um role específico
  const canManageRole = (targetRole) => getRoleLevel(user?.role) > getRoleLevel(targetRole);

  // Pode ver licenciamento (master)
  const canViewLicenses = () => getRoleLevel(user?.role) >= getRoleLevel('master');

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      loginForceSession, 
      logout, 
      acceptTerms,
      needsTermsAcceptance,
      isMaster,
      isAdminGeral,
      isAdmin, 
      isMarcadorGeral,
      isSecretaria,
      isRelator,
      getRelatorOrigin,
      hasFullAccess,
      canViewSlots,
      canEditSlots,
      canViewReports,
      canViewAudit,
      canManageUsers,
      canViewSettings,
      canViewUnits,
      canViewProcedimentos,
      canViewLicenses,
      canViewDashboard,
      canViewPatients,
      canViewAppointments,
      canManageRole,
      getRoleLevel,
      licenseStatus
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
